<script setup lang="ts">
import { computed } from "vue";
import { Accordion } from "~/components/clarity-ui/Accordion";
import { AccordionDetails } from "~/components/clarity-ui/Accordion/AccordionDetails";
import { AccordionSummary } from "~/components/clarity-ui/Accordion/AccordionSummary";
import useDevice from "~/composables/useDevice";
import { containsLink, mapNodes } from "~/utils/helper/magnolia";
import FooterColumnContent from "./FooterColumnContent.vue";
import LinkComponent from "~/templates/components/cms/LinkComponent.vue";

export interface FooterGridProps {
  row: object;
}

const { row } = defineProps<FooterGridProps>();

const { isTabletOrMobile } = useDevice();

const columns = computed(() => mapNodes(row).concat(Array(4).fill({})).slice(0, 4));
</script>

<template>
  <template v-if="isTabletOrMobile && columns[0]?.displayAs === 'link'">
    <nav>
      <Accordion size="small">
        <template v-for="column in columns" :key="'headline' + column?.['@id']">
          <AccordionDetails v-if="Object.keys(column).length > 0 && !containsLink(column?.link)">
            <AccordionSummary>{{ column?.title }}</AccordionSummary>
            <FooterColumnContent :column="column" />
          </AccordionDetails>
        </template>
      </Accordion>
    </nav>
  </template>
  <template v-else>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 xl:gap-6">
      <template v-for="column in columns" :key="'headline' + column?.['@id']">
        <LinkComponent
          class="footer-grid-cell"
          :class="{ 'font-medium': column?.displayAs !== 'button' }"
          :link="column?.link"
          optional-link-classes="cursor-pointer hover:text-dark-blue"
          >{{ column?.title }}</LinkComponent
        >
        <FooterColumnContent class="footer-grid-cell" :column="column" />
      </template>
    </div>
  </template>
</template>

<style>
@screen md {
  .footer-grid-cell:nth-child(2n + 1) {
    order: 1;
  }

  .footer-grid-cell:nth-child(2n + 2) {
    order: 2;
  }

  .footer-grid-cell:nth-child(2n + 5) {
    order: 3;
  }

  .footer-grid-cell:nth-child(2n + 6) {
    order: 4;
  }

  .footer-grid-cell:nth-child(2n + 9) {
    order: 5;
  }

  .footer-grid-cell:nth-child(2n + 10) {
    order: 6;
  }

  .footer-grid-cell:nth-child(2n + 13) {
    order: 7;
  }

  .footer-grid-cell:nth-child(2n + 14) {
    order: 8;
  }
}

@screen lg {
  .footer-grid-cell:nth-child(2n + 1) {
    order: 1;
  }

  .footer-grid-cell:nth-child(2n + 2) {
    order: 2;
  }

  .footer-grid-cell:nth-child(2n + 9) {
    order: 3;
  }

  .footer-grid-cell:nth-child(2n + 10) {
    order: 4;
  }
}
</style>
